import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as S from './styled';

const DEFAULT_AUTOPLAY_MS = 4000;
const SWIPE_THRESHOLD = 50;

export type DotColors = {
  active?: string;
  inactive?: string;
  hover?: string;
};

type Props = {
  children: ReactNode;
  autoplayMs?: number;
  dotColors?: DotColors;
};

export const Slider = ({ children, autoplayMs = DEFAULT_AUTOPLAY_MS, dotColors }: Props) => {
  const slides = Array.isArray(children) ? children : [children];
  const slidesCount = slides.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  const goTo = (index: number) => {
    const next = (index + slidesCount) % slidesCount;
    setActiveIndex(next);
  };

  const goNext = () => goTo(activeIndex + 1);
  const goPrev = () => goTo(activeIndex - 1);

  useEffect(() => {
    if (isPaused || autoplayMs === 0) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slidesCount);
    }, autoplayMs);

    return () => clearInterval(interval);
  }, [isPaused, autoplayMs, slidesCount]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPaused(true);
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (touchDeltaX.current > SWIPE_THRESHOLD) {
      goPrev();
    } else if (touchDeltaX.current < -SWIPE_THRESHOLD) {
      goNext();
    }
    touchDeltaX.current = 0;
    setIsPaused(false);
  };

  return (
    <S.Slider onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <S.Viewport
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <S.Track $activeIndex={activeIndex}>
          {slides.map((slide, index) => (
            <S.TrackItem key={index}>{slide}</S.TrackItem>
          ))}
        </S.Track>
      </S.Viewport>

      <S.Dots role="tablist" aria-label="Slider pagination">
        {slides.map((_, index) => (
          <S.Dot
            key={index}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={`Go to slide ${index + 1}`}
            $isActive={index === activeIndex}
            $colors={dotColors}
            onClick={() => goTo(index)}
          />
        ))}
      </S.Dots>
    </S.Slider>
  );
};
